import { Column, ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, UserNameCell } from '@/components/core';
import {
  formatAttendanceDay,
  formatDuration,
  formatShiftEnd,
  formatSignedDuration,
  formatTimeOfDay,
  isOpenRecord,
} from '@/modules/time-attendance/attendance/services/attendance-rules';
import {
  DAY_STATUS_LABEL_KEY,
  DayStatusBadge,
  dayDisplayStatusOf,
  WORK_STATUSES,
} from '@/modules/time-attendance/attendance/components/day-status-badge/day-status-badge';
import { AttendanceRecord, RosterRow } from '../../types/manage-attendance.types';

interface CreateAttendanceTableColumnsProps {
  t: (key: string) => string;
  /** The employee's email for the name's subtitle; records do not carry it. */
  emailOf?: (userId: string) => string | undefined;
  /** Overtime column, only when overtime is on for the employee shown. */
  showOvertime?: boolean;
  /** Break time column, only for viewers who can take breaks or lunch themselves. */
  showBreak?: boolean;
  /** The departments the user may see (`useDepartmentScope().departments`), for the Department
   * column's dropdown filter. */
  departments?: readonly { ItemId: string; Title: string }[];
}

/** The Department column's filter: a dropdown of the departments the user may see, matched by the
 * department name; the list's department scope (by id) still applies on top. */
const departmentFilter = (departments: readonly { ItemId: string; Title: string }[]) => ({
  type: 'options' as const,
  dropdown: true,
  options: departments.map((department) => ({ value: department.Title, label: department.Title })),
});

const durationOrDash = (value?: number | null) => (value == null ? '-' : formatDuration(value));

/**
 * The day columns shared by the record list and the roster, reading the record from `pick`. Their ids
 * are Attendance fields: the record list (`onRecords`) sorts and filters on them on the server; the
 * roster pages employees, not records, so there they are neither sortable nor filterable.
 */
const dayColumns = <T,>(
  t: (key: string) => string,
  pick: (row: T) => AttendanceRecord | null,
  showOvertime = false,
  onRecords = true,
  showBreak = true
): ColumnDef<T>[] => [
  {
    // Time of day on the organization's clock, over a picked day range (see `ColumnFilterConfig`).
    id: 'StartTimeUtc',
    meta: onRecords ? { filter: { type: 'time', withDays: true } } : undefined,
    enableSorting: onRecords,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CLOCK_START_TIME')} />,
    accessorFn: (row) => {
      const record = pick(row);
      return record ? formatTimeOfDay(record.StartTimeUtc, true, record.Timezone) : '-';
    },
  },
  {
    id: 'EndTimeUtc',
    meta: onRecords ? { filter: { type: 'time', withDays: true } } : undefined,
    enableSorting: onRecords,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CLOCK_END_TIME')} />,
    accessorFn: (row) => {
      const record = pick(row);
      if (!record || isOpenRecord(record)) return '-';
      return formatShiftEnd(record.StartTimeUtc, record.EndTimeUtc, true, record.Timezone);
    },
  },
  ...(showBreak
    ? [
        {
          id: 'TotalBreakTimeInSecond',
          enableSorting: onRecords,
          header: ({ column }: { column: Column<T> }) => (
            <DataTableColumnHeader column={column} title={t('BREAK_TIME')} />
          ),
          accessorFn: (row: T) => durationOrDash(pick(row)?.TotalBreakTimeInSecond),
        },
      ]
    : []),
  {
    id: 'TotalWorkTimeInSecond',
    enableSorting: onRecords,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('TOTAL_WORK_TIME')} />,
    accessorFn: (row) => durationOrDash(pick(row)?.TotalWorkTimeInSecond),
  },
  ...(showOvertime
    ? [
        {
          id: 'OverTimeInSecond',
          header: ({ column }: { column: Column<T> }) => (
            <DataTableColumnHeader column={column} title={t('OVERTIME')} />
          ),
          enableSorting: onRecords,
          accessorFn: (row: T) => pick(row)?.OverTimeInSecond ?? null,
          cell: ({ row }: { row: { original: T } }) => {
            const seconds = pick(row.original)?.OverTimeInSecond;
            if (seconds == null) return '-';
            return (
              <span className={seconds < 0 ? 'text-destructive' : undefined}>
                {formatSignedDuration(seconds)}
              </span>
            );
          },
        },
      ]
    : []),
  {
    id: 'Status',
    enableSorting: onRecords,
    // Where the day was worked (WFH / WFO); an open day shows as working or a missing clock-out.
    meta: onRecords
      ? {
          filter: {
            type: 'options',
            options: WORK_STATUSES.map((status) => ({
              value: status,
              label: t(DAY_STATUS_LABEL_KEY[status]),
            })),
          },
        }
      : undefined,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => dayDisplayStatusOf(pick(row)) ?? '',
    cell: ({ row }) => {
      const record = pick(row.original);
      return (
        <DayStatusBadge
          status={dayDisplayStatusOf(record)}
          forced={Boolean(record?.IsForceClockOut)}
        />
      );
    },
  },
];

export const createAttendanceTableColumns = ({
  t,
  emailOf,
  showOvertime,
  showBreak,
  departments = [],
}: CreateAttendanceTableColumnsProps): ColumnDef<AttendanceRecord>[] => [
  {
    id: 'DisplayName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => row.DisplayName ?? '',
    cell: ({ row }) => (
      <UserNameCell name={row.original.DisplayName} email={emailOf?.(row.original.UserId)} />
    ),
  },
  {
    id: 'DepartmentName',
    meta: { filter: departmentFilter(departments), mobile: 'subtitle' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
    accessorFn: (row) => row.DepartmentName ?? '-',
  },
  {
    id: 'AttendanceDate',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('ATTENDANCE_DATE')} />,
    accessorFn: (row) => row.AttendanceDate,
    cell: ({ row }) => formatAttendanceDay(row.original.AttendanceDate),
  },
  ...dayColumns<AttendanceRecord>(t, (row) => row, showOvertime, true, showBreak),
];

/** One day for every employee in scope; employees with no record show "No record". The name and
 * department columns sort and filter the employees (UserProfileDetail fields) on the server. */
export const createRosterTableColumns = ({
  t,
  showBreak,
  departments = [],
}: CreateAttendanceTableColumnsProps): ColumnDef<RosterRow>[] => [
  {
    id: 'DisplayName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => row.employee.DisplayName ?? '',
    cell: ({ row }) => (
      <UserNameCell name={row.original.employee.DisplayName} email={row.original.employee.Email} />
    ),
  },
  {
    id: 'DepartmentName',
    meta: { filter: departmentFilter(departments), mobile: 'subtitle' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
    accessorFn: (row) => row.employee.DepartmentName ?? row.record?.DepartmentName ?? '-',
  },
  ...dayColumns<RosterRow>(t, (row) => row.record, false, false, showBreak),
];
